import React, { useState } from 'react';
import { Button, message } from 'antd';
import { Drawer } from '~/components';
import { RightOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import theme from '~/theme';
import { useTranslation } from 'react-i18next';
import InputPhone from './InputPhone';
import SelectAccounts from './SelectAccounts';
import Confirmation from './Confirmation';
import Result from './Result';
import { SendMoneyState, Transaction } from '~/types';
import InputAccountNumber from './InputAccountNumber';
import {
  checkBeneficiaryByAccountNumber,
  checkBeneficiaryByPhone,
  makeTransfer,
  SendMoneyBeneficiary,
} from '~/api/sendMoney';
import { TransactionProcessing } from '~/components';
import moment from 'moment';

const StyledButton = styled(Button)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  background: none;
  border: none;
  padding: 0;
  color: ${theme.textColor};
  background-color: ${theme.longButtonBg};
  padding: 32px 16px;
  border-radius: 8px;
  margin-bottom: 8px;
  font-size: 20px;
  font-weight: 600;
  &:hover,
  &:focus,
  &:active {
    background-color: ${theme.longButtonBg};
  }
`;

const ByPhoneOrAccountNumber = ({ isByAccount }: { isByAccount?: boolean }) => {
  const { t } = useTranslation('sendMoney');

  const [step, setStep] = useState<number>(1);
  // Steps:
  // 1 - input phone number or account number
  // 2 - select account if multiple found
  // 3 - confirmation
  // 4 - transaction processing / result

  const [mainState, setMainState] = useState<SendMoneyState>(initialState);

  const [isDrawerVisible, setIsDrawerVisible] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [beneficiaries, setBeneficiaries] = useState<SendMoneyBeneficiary[] | null>(null);
  const [transaction, setTransaction] = useState<Transaction | null>(null);

  const handleOpenDrawer = () => {
    setIsDrawerVisible(true);
  };

  const handleCloseDrawer = () => {
    setIsDrawerVisible(false);
    setStep(1);
    setMainState(initialState);
    setBeneficiaries(null);
    setTransaction(null);
  };

  const checkBeneficiary = async (number: string) => {
    const trimmedNumber = number.replace(/\s+/g, '');
    setLoading(true);
    try {
      const response = isByAccount
        ? await checkBeneficiaryByAccountNumber(trimmedNumber)
        : await checkBeneficiaryByPhone(trimmedNumber);
      setBeneficiaries(response);
      if (response.length === 1 && response[0].recipient.length === 1) {
        const beneficiary = response[0];
        setMainState((prevState) => ({
          ...prevState,
          recipientAccountId: beneficiary.recipient[0].accountId,
          recipientAccountNumber: beneficiary.recipient[0].accountNumber,
          recipientAccountCurrency: beneficiary.recipient[0].currency,
          selectedBeneficiary: 0,
        }));
        setStep(3);
      } else {
        setStep(2);
      }
    } catch (error: any) {
      if (error.response?.data?.message) {
        void message.error(error?.response?.data?.message);
      } else {
        message.error(t('beneficiaryNotFound'));
      }
    } finally {
      setLoading(false);
    }
  };

  const createTransfer = async () => {
    setLoading(true);
    try {
      const response = await makeTransfer(mainState.consentId!);
      setTransaction(response);
      setStep(4);
    } catch (error: any) {
      if (error) {
        void message.error(error?.response?.data?.message);
      } else {
        message.error(t('transferError'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleContinue = (number: string) => {
    checkBeneficiary(number);
  };

  const handleTransfer = async () => {
    await createTransfer();
  };

  const transactionDate = transaction?.createdAt
    ? moment(transaction?.createdAt).format('LL LT')
    : '';
  return (
    <>
      <StyledButton type="text" block onClick={handleOpenDrawer}>
        {isByAccount ? t('byAccountNumber') : t('byPhoneNumber')}
        <RightOutlined style={{ color: theme.longButtonArrow, fontSize: '16px' }} />
      </StyledButton>
      <Drawer
        size="large"
        placement="bottom"
        open={isDrawerVisible}
        onClose={handleCloseDrawer}
        headerStyle={{
          backgroundColor: step !== 4 ? theme.layoutBodyBackground : undefined,
          fontWeight: 400,
        }}
        bodyStyle={{
          padding: '24px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          backgroundColor: step !== 4 ? theme.layoutBodyBackground : undefined,
        }}
        title={step !== 4 ? (step === 2 ? t('selectAccount') : t('sendMoney')) : transactionDate}
      >
        {transaction ? (
          <TransactionProcessing
            onComplete={(t) => {
              setTransaction((prev) => (!prev ? prev : t));
            }}
            transactionId={transaction.id}
            completeComponent={<Result trx={transaction} />}
            fullWidth
          />
        ) : (
          <div style={{ maxWidth: '760px', width: '100%' }}>
            {step === 1 &&
              (isByAccount ? (
                <InputAccountNumber
                  mainState={mainState}
                  setMainState={setMainState}
                  onNext={handleContinue}
                  isLoading={loading}
                />
              ) : (
                <InputPhone
                  mainState={mainState}
                  setMainState={setMainState}
                  onNext={handleContinue}
                  isLoading={loading}
                />
              ))}
            {step === 2 && (
              <SelectAccounts
                mainState={mainState}
                setMainState={setMainState}
                beneficiaries={beneficiaries!}
                onBack={() => {
                  setStep(1);
                  setMainState((prevState) => ({
                    ...prevState,
                    recipientAccountNumber: undefined,
                    recipientAccountId: undefined,
                  }));
                }}
                onNext={() => setStep(3)}
              />
            )}
            {step === 3 && (
              <Confirmation
                mainState={mainState}
                setMainState={setMainState}
                onTransfer={handleTransfer}
                beneficiaries={beneficiaries!}
                isLoading={loading}
                setIsLoading={setLoading}
              />
            )}
          </div>
        )}
      </Drawer>
    </>
  );
};

const initialState: SendMoneyState = {
  recipientPhoneNumber: undefined,
  recipientAccountNumber: undefined,
  recipientAccountId: undefined,
  recipientAccountCurrency: undefined,
  senderAccount: undefined,
  senderAccountId: undefined,
  senderAccountNumber: undefined,
  amount: { value: undefined, currency: undefined },
  consentId: undefined,
  note: undefined,
  selectedBeneficiary: undefined,
};

export default ByPhoneOrAccountNumber;
